import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Plane, ShieldCheck, Clock, Car, MapPin, ArrowRight, Phone, MessageSquare } from "lucide-react";

const CANONICAL = "https://www.aspenexecutivelimos.com/airport-transportation";
const SEO_TITLE = "Aspen Airport Transportation & Car Service | Aspen Executive Limo";
const SEO_DESC = "Private Aspen airport transportation and car service. Chauffeured luxury SUV transfers to and from Aspen (ASE), Eagle (EGE), Denver (DEN), Rifle & Grand Junction airports — Denver to Aspen transportation done right.";
const SEO_KEYWORDS = "Aspen airport transportation, Aspen airport car service, Denver to Aspen transportation, Eagle Airport transportation, private airport transportation Aspen, Aspen limo service, ASE airport transfer";

function useAirportSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = SEO_TITLE;

    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      el.setAttribute("data-airport-seo", "true");
      return el;
    };

    setMeta("name", "description", SEO_DESC);
    setMeta("name", "keywords", SEO_KEYWORDS);
    setMeta("property", "og:title", SEO_TITLE);
    setMeta("property", "og:description", SEO_DESC);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", CANONICAL);
    setMeta("property", "og:image", HERO_IMG);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", SEO_TITLE);
    setMeta("name", "twitter:description", SEO_DESC);
    setMeta("name", "twitter:image", HERO_IMG);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const hadCanonical = !!canonical;
    const prevHref = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", CANONICAL);

    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://www.aspenexecutivelimos.com/#business",
      name: "Aspen Executive Limo",
      description: SEO_DESC,
      url: CANONICAL,
      image: HERO_IMG,
      telephone: "+1-970-315-6500",
      priceRange: "$$$",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Aspen",
        addressRegion: "CO",
        addressCountry: "US",
      },
      areaServed: [
        { "@type": "City", name: "Aspen, CO" },
        { "@type": "City", name: "Vail, CO" },
        { "@type": "City", name: "Denver, CO" },
        { "@type": "Airport", name: "Aspen/Pitkin County Airport", iataCode: "ASE" },
        { "@type": "Airport", name: "Eagle County Regional Airport", iataCode: "EGE" },
        { "@type": "Airport", name: "Denver International Airport", iataCode: "DEN" },
        { "@type": "Airport", name: "Garfield County Regional Airport", iataCode: "RIL" },
        { "@type": "Airport", name: "Grand Junction Regional Airport", iataCode: "GJT" },
      ],
      makesOffer: {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Private Airport Transportation",
          serviceType: "Private chauffeured luxury SUV airport transfers",
          areaServed: "Roaring Fork Valley, Colorado",
        },
      },
    };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aspenexecutivelimos.com/" },
        { "@type": "ListItem", position: 2, name: "Airport Transportation", item: CANONICAL },
      ],
    };

    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.schemaAnswer || f.a },
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-airport-seo", "true");
    script.text = JSON.stringify([ld, breadcrumb, faqPage]);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      document.head.querySelectorAll('[data-airport-seo="true"]').forEach((n) => n.remove());
      if (hadCanonical && canonical) {
        const restored = document.createElement("link");
        restored.setAttribute("rel", "canonical");
        if (prevHref) restored.setAttribute("href", prevHref);
        document.head.appendChild(restored);
      }
    };
  }, []);
}

const HERO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_luxury-transport-hub-5/artifacts/q7o0bjqb_SUV%20Plane.%2003png.png";
const PHONE = "+19703156500";

const AIRPORTS = [
  { code: "ASE", name: "Aspen / Pitkin County" },
  { code: "EGE", name: "Eagle County (Vail)" },
  { code: "DEN", name: "Denver International" },
  { code: "RIL", name: "Rifle / Garfield County" },
  { code: "GJT", name: "Grand Junction Regional" },
];

const FEATURES = [
  { icon: Car, title: "Luxury 4WD SUVs", desc: "Immaculate four-wheel-drive SUVs built for Colorado's mountain roads and winter weather." },
  { icon: MapPin, title: "Local Chauffeurs", desc: "Knowledgeable, professional local drivers who know every route, pass, and shortcut." },
  { icon: ShieldCheck, title: "Safety First", desc: "Fully licensed and insured, with meticulously maintained vehicles for total peace of mind." },
  { icon: Clock, title: "Always Punctual", desc: "Flight-tracked pickups and reliable, on-time service built around your schedule." },
];

const CTA_BTN = "inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold text-base tracking-wide rounded-sm px-[3px] py-[1px] shadow-xl hover:bg-[#E0BC4A] transition-colors";

const FAQS = [
  {
    q: "How much does transportation from Denver to Aspen cost?",
    a: "Denver to Aspen is one of our most requested routes. Rather than a one-size-fits-all fare, we tailor every trip to your group size, luggage, and schedule. You can review our published flat rates on our Rates page, and we're always happy to prepare a custom quote for any route that isn't listed.",
    schemaAnswer: "Denver to Aspen is one of our most requested routes. We tailor every trip to your group size, luggage, and schedule. You can review our published flat rates on our Rates page, and we are happy to prepare a custom quote for any route that isn't listed.",
    link: { to: "/rates", label: "View our Rates", testid: "faq-link-rates" },
  },
  {
    q: "Do you provide Eagle Airport (EGE) transportation?",
    a: "Yes. We provide luxury SUV transfers to and from Eagle County Regional Airport (EGE) near Vail, along with Aspen (ASE), Denver (DEN), Rifle (RIL), and Grand Junction (GJT).",
  },
  {
    q: "Do you provide transportation for private jet passengers?",
    a: "Absolutely. We meet private jet passengers planeside at the FBO for a truly seamless arrival and departure, handling your luggage and timing everything around your flight.",
  },
  {
    q: "Do you operate during Colorado winter weather?",
    a: "Yes. Our fleet of four-wheel-drive SUVs and our experienced local chauffeurs are built for Colorado's mountain roads and winter conditions, so you arrive safely and on time all year round.",
  },
  {
    q: "How do I request a quote?",
    a: "Requesting a quote takes just a minute. Share your trip details through our online Request a Quote form, or simply call or text us directly at (970) 315-6500 — we're available 24/7.",
    link: { to: "/reserve", label: "Request a Quote", testid: "faq-link-quote" },
  },
];

export default function AirportTransportation() {
  useAirportSEO();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen" data-testid="airport-transportation-page">
      <Navbar />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img src={HERO_IMG} alt="Luxury SUV beside a private jet" className="w-full h-full object-cover object-center" style={{ filter: "brightness(1.35) saturate(1.15) contrast(1.02)" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-[120px] pb-24 sm:pt-[160px] sm:pb-32">
          <p className="text-xs tracking-label uppercase font-bold text-[#D4AF37] mb-4" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>Aspen Executive Limo</p>
          <h1 className="font-heading text-5xl sm:text-7xl font-light leading-[1.05] tracking-tight text-white max-w-3xl" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>
            Luxury Airport Transportation
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-zinc-100 font-light leading-relaxed" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}>
            Seamless, chauffeured SUV transfers to and from every airport in the Roaring Fork Valley and beyond — arriving in comfort, on time, every time.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/reserve" data-testid="airport-reserve-btn" className={CTA_BTN}>Reserve Your Ride <ArrowRight size={16} /></Link>
            <a href={`sms:${PHONE}`} data-testid="airport-calltext-btn" className={CTA_BTN}><MessageSquare size={16} /> Call or Text Now</a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Elite Service at Unmatched Value</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
          From private jet arrivals to commercial flights, Aspen Executive Limo delivers a first-class airport experience in our luxury four-wheel-drive SUVs. Our knowledgeable local chauffeurs handle every detail — flight tracking, door-to-door curbside pickup, and your luggage — so you can simply relax and enjoy the ride through the Colorado Rockies.
        </p>
      </section>

      {/* AIRPORTS SERVED */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Airports We Serve</h2>
          <p className="mt-4 text-center text-zinc-400 font-light">Reliable transfers to and from every major airport in the region.</p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4">
            {AIRPORTS.map((a) => (
              <div key={a.code} data-testid={`airport-${a.code}`} className="bg-[#101012] border border-white/10 rounded-sm p-6 text-center hover:border-[#D4AF37]/40 transition-colors">
                <Plane size={22} className="gold-text mx-auto mb-3" />
                <div className="font-heading text-3xl text-white">{a.code}</div>
                <div className="mt-1 text-xs text-zinc-400 tracking-wide">{a.name}</div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-zinc-300 font-light max-w-2xl mx-auto">
            <span className="gold-text font-medium">Private Jet &amp; FBO transfers</span> — we meet you planeside at the FBO for a truly seamless arrival and departure.
          </p>
          <p className="mt-4 text-center text-zinc-300 font-light max-w-2xl mx-auto" data-testid="airport-fbo-fee-note">
            Private jet arrivals and departures may be subject to FBO facility fees charged by the airport or FBO. These fees are separate from Aspen Executive Limo's transportation charges.
          </p>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Why Ride With Us</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`airport-feature-${f.title}`} className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500">
              <f.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
              <h3 className="font-heading text-xl mb-2 text-white">{f.title}</h3>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="airport-faq">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Frequently Asked Questions</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <div className="mt-10 space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`faq-item-${i}`} className="bg-[#101012] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/40 transition-colors">
              <h3 className="font-heading text-xl text-white">{f.q}</h3>
              <p className="mt-3 text-zinc-400 font-light text-sm leading-relaxed">{f.a}</p>
              {f.link && (
                <Link to={f.link.to} data-testid={f.link.testid} className="mt-4 inline-flex items-center gap-1.5 gold-text text-sm font-medium hover:gap-2.5 transition-all">
                  {f.link.label} <ArrowRight size={14} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA + INTERNAL LINKS */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Reserve Your Airport Transfer</h2>
          <p className="mt-4 text-zinc-300 font-light">Request a quote in minutes, or call/text us directly — available 24/7.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/reserve" data-testid="airport-cta-reserve" className={CTA_BTN}>Reserve Your Ride <ArrowRight size={16} /></Link>
            <a href={`tel:${PHONE}`} data-testid="airport-cta-call" className={CTA_BTN}><Phone size={16} /> Call or Text Now</a>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm">
            <Link to="/" data-testid="airport-link-home" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">Home <ArrowRight size={14} /></Link>
            <Link to="/fleet" data-testid="airport-link-fleet" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">View Our Fleet <ArrowRight size={14} /></Link>
            <Link to="/rates" data-testid="airport-link-rates" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">See Our Rates <ArrowRight size={14} /></Link>
            <Link to="/reserve" data-testid="airport-link-quote" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">Request a Quote <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
